import { useMemo, useState } from 'react'
import type { GameSide } from '../chatv1/transport'
import { FILES, MARKED, RANKS, fileNumber, glyphOf, nameOf, parsePoint, piecesOf, pointName, targetsFrom } from './xiangqiGeom'
import { HAN_FONT, inkDrop } from './hanText'

/**
 * THE XIANGQI BOARD — owner, 2026-10-20: 開始做中國象棋.
 *
 * Nine lines by ten on wood, the river across the middle (楚河 漢界), the two palaces with their diagonals, and the
 * marks where the cannons and soldiers start; round pieces with their traditional characters, Red's in red ink and
 * Black's in black. Your side is at the bottom. Tap one of your pieces, then one of the points it may go to (the dots
 * — rings where it would take); the points come from the service, which referees every move. The last move is marked
 * on both of its points, a general in check glows, and your agent's hint is a blue arrow.
 *
 * The file numbers are WXF's, each side counting from its own right — the numbers the move list is written in.
 */

export type XiangqiMove = { from: string; to: string }

/** Board units: points at 0…8 across and 0…9 down, a margin of one for the numbers. */
const PAD = 1
const VW = FILES - 1 + PAD * 2
const VH = RANKS - 1 + PAD * 2
const R = 0.44
/** The characters' sizes, in board units: on a piece, and the river's words. */
const HAN_SIZE = 0.5
const RIVER_SIZE = 0.5

export function XiangqiBoard({
  fen,
  orientation,
  movable,
  legal,
  last,
  check,
  hint,
  onMove,
}: {
  fen: string
  /** The viewer's side, at the bottom. */
  orientation: GameSide
  /** The side the viewer may move now; null while it is not their move (or the game is over). */
  movable: GameSide | null
  /** The service's legal moves for the side to move ("h2e2"). */
  legal: readonly string[]
  last: { from: string; to: string } | null
  check: boolean
  /** Your agent's suggestion, drawn as an arrow. */
  hint: { from: string; to: string } | null
  onMove: (m: XiangqiMove) => void
}) {
  const pieces = useMemo(() => piecesOf(fen), [fen])
  const [selected, setSelected] = useState<string | null>(null)
  // A new position (a move landed, or the game was re-read) clears a half-made choice.
  const [seenFen, setSeenFen] = useState(fen)
  if (seenFen !== fen) {
    setSeenFen(fen)
    setSelected(null)
  }
  const targets = useMemo(
    () => (selected && movable ? targetsFrom(legal, selected) : new Set<string>()),
    [legal, selected, movable],
  )
  const turn: GameSide = fen.split(' ')[1] === 'b' ? 'b' : 'w'
  const inCheck = check ? [...pieces].find(([, p]) => p.type === 'k' && p.side === turn)?.[0] ?? null : null

  /** A point's place on the screen, the viewer's side at the bottom. */
  const at = (p: string): [number, number] => {
    const [f, r] = parsePoint(p)!
    return orientation === 'w' ? [PAD + f, PAD + (RANKS - 1 - r)] : [PAD + (FILES - 1 - f), PAD + r]
  }
  /** The point at a screen column and row. */
  const pointAt = (col: number, row: number): string =>
    orientation === 'w' ? pointName(col, RANKS - 1 - row) : pointName(FILES - 1 - col, row)

  const tap = (p: string) => {
    if (!movable) return
    if (selected && targets.has(p)) {
      setSelected(null)
      onMove({ from: selected, to: p })
      return
    }
    const piece = pieces.get(p)
    setSelected(piece && piece.side === movable && p !== selected ? p : null)
  }

  const other: GameSide = orientation === 'w' ? 'b' : 'w'
  // The hint's arrow, from the edge of the piece (its character stays readable) to just short of the point.
  const arrow = (() => {
    if (!hint || !parsePoint(hint.from) || !parsePoint(hint.to)) return null
    const [a, b] = [at(hint.from), at(hint.to)]
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1
    const [ux, uy] = [(b[0] - a[0]) / len, (b[1] - a[1]) / len]
    return { x1: a[0] + ux * R, y1: a[1] + uy * R, x2: b[0] - ux * 0.3, y2: b[1] - uy * 0.3 }
  })()
  const marks = MARKED.flatMap((p) => {
    const [x, y] = at(p)
    const col = x - PAD
    const out: string[] = []
    for (const dx of [-1, 1]) {
      if (col + dx < 0 || col + dx > FILES - 1) continue
      for (const dy of [-1, 1]) {
        const g = 0.09
        const l = 0.2
        out.push(`M${x + dx * g},${y + dy * (g + l)} L${x + dx * g},${y + dy * g} L${x + dx * (g + l)},${y + dy * g}`)
      }
    }
    return out
  })

  return (
    <div className="xb-wrap">
      <div className={`xb-board${movable ? ' xb-live' : ''}`}>
        <svg className="xb-svg" viewBox={`0 0 ${VW} ${VH}`} aria-hidden="true">
          <defs>
            <radialGradient id="xb-face" cx="38%" cy="32%" r="72%">
              <stop offset="0%" stopColor="#fff8e8" />
              <stop offset="70%" stopColor="#f1dfb8" />
              <stop offset="100%" stopColor="#d6b98a" />
            </radialGradient>
            <radialGradient id="xb-light" cx="25%" cy="15%" r="65%">
              <stop offset="0%" stopColor="rgba(255, 236, 196, 0.45)" />
              <stop offset="100%" stopColor="rgba(255, 236, 196, 0)" />
            </radialGradient>
            <pattern id="xb-grain" width="0.42" height={VH} patternUnits="userSpaceOnUse" patternTransform="rotate(7)">
              <rect width="0.1" height={VH} fill="rgba(120, 72, 24, 0.07)" />
            </pattern>
            <marker id="xb-head" viewBox="0 0 4 4" refX="2" refY="2" markerWidth="2.4" markerHeight="2.4" orient="auto">
              <path d="M0,0 L4,2 L0,4 Z" className="xb-hint-head" />
            </marker>
          </defs>
          <rect className="xb-wood" x="0" y="0" width={VW} height={VH} />
          <rect x="0" y="0" width={VW} height={VH} fill="url(#xb-grain)" />
          <rect x="0" y="0" width={VW} height={VH} fill="url(#xb-light)" />
          <rect className="xb-frame" x={PAD - 0.14} y={PAD - 0.14} width={FILES - 1 + 0.28} height={RANKS - 1 + 0.28} />
          <g className="xb-line">
            {Array.from({ length: RANKS }, (_, i) => (
              <line key={`r${i}`} x1={PAD} y1={PAD + i} x2={PAD + FILES - 1} y2={PAD + i} />
            ))}
            {Array.from({ length: FILES }, (_, i) =>
              i === 0 || i === FILES - 1 ? (
                <line key={`f${i}`} x1={PAD + i} y1={PAD} x2={PAD + i} y2={PAD + RANKS - 1} />
              ) : (
                // The river cuts the inner files.
                <g key={`f${i}`}>
                  <line x1={PAD + i} y1={PAD} x2={PAD + i} y2={PAD + 4} />
                  <line x1={PAD + i} y1={PAD + 5} x2={PAD + i} y2={PAD + RANKS - 1} />
                </g>
              ),
            )}
            {[0, 7].map((top) => (
              <g key={`p${top}`}>
                <line x1={PAD + 3} y1={PAD + top} x2={PAD + 5} y2={PAD + top + 2} />
                <line x1={PAD + 5} y1={PAD + top} x2={PAD + 3} y2={PAD + top + 2} />
              </g>
            ))}
            <path className="xb-mark" d={marks.join(' ')} />
          </g>
          <g className="xb-river" fontFamily={HAN_FONT} fontSize={RIVER_SIZE}>
            {(['楚 河', '漢 界'] as const).map((words, i) => (
              <text key={words} x={PAD + 2 + i * 4} y={PAD + 4.5 + inkDrop(words) * RIVER_SIZE}>
                {words}
              </text>
            ))}
          </g>
          {Array.from({ length: FILES }, (_, col) => {
            const file = orientation === 'w' ? col : FILES - 1 - col
            return (
              <g key={`n${col}`} className="xb-coord">
                <text x={PAD + col} y={VH - 0.3}>
                  {fileNumber(file, orientation)}
                </text>
                <text x={PAD + col} y={0.52}>
                  {fileNumber(file, other)}
                </text>
              </g>
            )
          })}
          {last && parsePoint(last.from) && (
            <circle className="xb-last-from" cx={at(last.from)[0]} cy={at(last.from)[1]} r={0.2} />
          )}
          {[...pieces].map(([p, piece]) => {
            const [x, y] = at(p)
            const lift = p === selected ? -0.08 : 0
            return (
              <g key={p} className={`xb-piece xb-${piece.side}`} transform={`translate(${x} ${y + lift})`}>
                <circle className="xb-shadow" cx={0.04} cy={0.07 - lift} r={R} />
                <circle className="xb-disc" r={R} fill="url(#xb-face)" />
                <circle className="xb-rim" r={R - 0.07} />
                <text className="xb-han" fontFamily={HAN_FONT} fontSize={HAN_SIZE} y={inkDrop(glyphOf(piece), 700) * HAN_SIZE}>
                  {glyphOf(piece)}
                </text>
                {p === inCheck && <circle className="xb-check" r={R + 0.05} />}
                {last && p === last.to && <circle className="xb-last" r={R + 0.04} />}
                {p === selected && <circle className="xb-sel" r={R + 0.05} />}
              </g>
            )
          })}
          {[...targets].map((p) => {
            const [x, y] = at(p)
            return pieces.has(p) ? (
              <circle key={`t${p}`} className="xb-take" cx={x} cy={y} r={R + 0.04} />
            ) : (
              <circle key={`t${p}`} className="xb-dot" cx={x} cy={y} r={0.12} />
            )
          })}
          {arrow && <line className="xb-hint" {...arrow} markerEnd="url(#xb-head)" />}
        </svg>
        <div
          className="xb-hits"
          style={{
            left: `${((PAD - 0.5) / VW) * 100}%`,
            right: `${((PAD - 0.5) / VW) * 100}%`,
            top: `${((PAD - 0.5) / VH) * 100}%`,
            bottom: `${((PAD - 0.5) / VH) * 100}%`,
          }}
        >
          {Array.from({ length: RANKS }, (_, row) =>
            Array.from({ length: FILES }, (_, col) => {
              const p = pointAt(col, row)
              const piece = pieces.get(p)
              return (
                <button
                  key={p}
                  type="button"
                  className="xb-pt"
                  aria-label={`${p}, ${piece ? nameOf(piece) : 'empty'}${targets.has(p) ? (piece ? ', take' : ', move here') : ''}`}
                  aria-pressed={p === selected ? true : undefined}
                  aria-disabled={!movable}
                  onClick={() => tap(p)}
                />
              )
            }),
          )}
        </div>
      </div>
    </div>
  )
}
